<script lang="ts" setup>
import type { BlockSerializedSlotOutlet } from '#layers/block-engine/types/block';
import { tv } from 'tailwind-variants';
import { computed } from 'vue';

import { useBlockEditorContext } from '#layers/block-engine/composables/use-block-editor-context';
import { slotOutletBlockId } from '#layers/block-engine/utils/block/const';

const {
	outlet,
} = defineProps<{
	outlet: BlockSerializedSlotOutlet;
	ui?: Partial<typeof uiVariants.slots>;
}>();

const uiVariants = tv({
	slots: {
		outlet: `
			m-px inline-flex w-full min-w-0 flex-1 items-center justify-center gap-1 rounded-md border-2 border-dashed p-2
			text-primary italic transition-colors duration-150 select-none
		`,
	},
	variants: {
		selected: { true: {} },
		hovered: { true: {} },
	},
	compoundVariants: [
		{
			selected: true,
			class: {
				outlet: 'border-primary',
			},
		},
		{
			selected: false,
			hovered: true,
			class: {
				outlet: 'border-primary/50',
			},
		},
		{
			selected: false,
			hovered: false,
			class: {
				outlet: 'border-primary/20',
			},
		},
	],
});

const { selection, drag, insertion } = useBlockEditorContext();

// Select

function onClick(event: MouseEvent) {
	if (!drag.dragging.value) {
		if (event.ctrlKey) {
			selection.toggle(outlet.id);
		}
		else {
			selection.selectedChildIds.value = [outlet.id];
		}

		insertion.clear();

		event.stopPropagation();
	}
}

const selected = computed(() => selection.selectedChildIds.value.includes(outlet.id));
const hovered = computed(() => !drag.dragging.value && selection.hoveredChildId.value === outlet.id);

// CSS

const uiClass = computed(() => uiVariants({
	selected: selected.value,
	hovered: hovered.value,
}));
</script>

<template>
	<div class="relative">
		<BuilderBlockSelection
			:child="outlet"
			:block="{
				id: slotOutletBlockId,
				name: $t('builder.blocks.slot_outlet.name'),
			}"
			:selected
			:ui="{
				border: 'hidden',
			}"
		/>

		<div
			:class="uiClass.outlet({ class: ui?.outlet })"
			@mouseover.stop.prevent="selection.hoveredChildId.value = outlet.id"
			@mouseout.stop.prevent="selection.hoveredChildId.value = null"
			@mouseup.prevent="onClick"
		>
			<DIcon
				name="lucide:scan-text"
			/>
			<span>{{ outlet.slotName }}</span>
		</div>
	</div>
</template>
